/* Same-browser Pixelback checkout recovery. Stored context never grants access:
   the worker must confirm this checkout's token before any return or cart write. */
(function () {
  'use strict';
  const config = document.getElementById('pixelback-return-script')?.dataset;
  if (!config) return;
  const RETURN = 'robopixel_pixelback_return';
  const CONTEXT = 'robopixel_customizer_context';
  const TOKEN = 'robopixel_pixelback_token';
  const CART = 'robopixel_cart_restore';
  const MAX_AGE = 2 * 60 * 60 * 1000;
  const methods = ['upload', 'portrait', 'prompt', 'outfit'];
  const read = key => { try { return JSON.parse(localStorage.getItem(key)); } catch (_) { return null; } };
  const write = (key, value) => { try { localStorage.setItem(key, JSON.stringify(value)); } catch (_) {} };
  const persist = (key, value) => {
    const serialized = JSON.stringify(value);
    localStorage.setItem(key, serialized);
    if (localStorage.getItem(key) !== serialized) throw new Error('storage_unavailable');
  };
  const remove = key => { try { localStorage.removeItem(key); } catch (_) {} };
  const fresh = value => value && Number.isFinite(value.t) && Date.now() >= value.t && Date.now() - value.t < MAX_AGE;
  const clean = value => typeof value === 'string' ? value.slice(0, 100) : '';
  const current = () => new URL(window.location.href);
  const onCustomizer = () => /\/pages\/customizer\/?$/.test(current().pathname);

  function remember(context) {
    if (!onCustomizer()) return;
    const params = current().searchParams;
    const value = context || Object.fromEntries(['brand', 'model', 'method'].map(key => [key, params.get(key)]));
    // Don't overwrite a useful selection just because the picker/landing opened.
    if (!value.brand || !value.model) return;
    write(CONTEXT, { brand: clean(value.brand), model: clean(value.model),
      method: methods.includes(value.method) ? value.method : '',
      pair: params.get('pair_second') === '1', t: Date.now() });
  }

  async function request(url, options = {}) {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 5000);
    try {
      const response = await fetch(url, { ...options, signal: controller.signal });
      if (!response.ok) throw new Error('pixelback_return_request_failed');
      return await response.json();
    } finally { clearTimeout(timer); }
  }

  // Called by BOTH purchase CTAs, only after the isolated checkout is created.
  async function prepare(token, context, variantId) {
    remember(context);
    const saved = read(CONTEXT);
    const selected = fresh(saved) ? saved : {};
    // Keep an independent correlation token: another tab may activate access
    // and remove TOKEN before Continue shopping reaches the storefront.
    const pending = { brand: selected.brand || '', model: selected.model || '',
      method: selected.method || '', pair: !!selected.pair, token, t: Date.now() };
    try {
      const cart = await request(config.cartUrl + '.js', { cache: 'no-store' });
      if (!Array.isArray(cart.items)) throw new Error('cart_snapshot_invalid');
      const items = cart.items.filter(item => Number(item.variant_id) !== Number(variantId))
        .map(item => ({ id: item.variant_id, quantity: item.quantity, properties: item.properties || {},
          ...(item.selling_plan_allocation ? { selling_plan: item.selling_plan_allocation.selling_plan.id } : {}) }));
      if (items.some(item => !Number.isSafeInteger(item.id) || item.id <= 0 ||
          !Number.isSafeInteger(item.quantity) || item.quantity <= 0)) throw new Error('cart_snapshot_invalid');
      const previousCart = localStorage.getItem(CART), previousReturn = localStorage.getItem(RETURN);
      try {
        // Commit only after the full cart has been read. Even an empty snapshot
        // proves that backup succeeded, rather than silently losing a failed read.
        persist(CART, { items, token, t: pending.t });
        persist(RETURN, pending);
      } catch (error) {
        for (const [key, previous] of [[CART, previousCart], [RETURN, previousReturn]]) {
          try { previous === null ? localStorage.removeItem(key) : localStorage.setItem(key, previous); } catch (_) {}
        }
        throw error;
      }
    } catch (_) {
      const error = new Error(document.documentElement?.lang?.startsWith('de')
        ? 'Dein Warenkorb und Checkout-Fortschritt konnten nicht gespeichert werden. Bitte versuche es erneut, bevor du zur Kasse gehst.'
        : 'We couldn’t save your cart and checkout progress. Please try again before continuing to checkout.');
      error.code = 'pixelback_backup_failed';
      throw error;
    }
  }

  const lineIdentity = item => JSON.stringify([
    String(item.variant_id || item.id),
    String(item.selling_plan_allocation?.selling_plan?.id || item.selling_plan || ''),
    Object.entries(item.properties || {}).filter(([,value]) => value != null && value !== '')
      .map(([key,value]) => [key, String(value)]).sort(([a], [b]) => a.localeCompare(b))
  ]);
  function missingItems(snapshot, cart) {
    if (!Array.isArray(cart.items)) throw new Error('cart_restore_invalid');
    const quantities = new Map();
    for (const item of cart.items) {
      const key = lineIdentity(item);
      quantities.set(key, (quantities.get(key) || 0) + item.quantity);
    }
    return snapshot.items.flatMap(item => {
      const key = lineIdentity(item), available = quantities.get(key) || 0;
      quantities.set(key, Math.max(0, available - item.quantity));
      return available < item.quantity ? [{ ...item, quantity: item.quantity - available }] : [];
    });
  }
  async function restoreCart(token, stillCurrent) {
    const snapshot = read(CART);
    if (!snapshot) return;
    if (snapshot.token && snapshot.token !== token) return;
    if (!fresh(snapshot) || !Array.isArray(snapshot.items) || !snapshot.items.length ||
        snapshot.items.some(item => !Number.isSafeInteger(item.id) || item.quantity <= 0)) { remove(CART); return; }
    const cart = await request(config.cartUrl + '.js', { cache: 'no-store' });
    if (!stillCurrent()) return;
    if (cart.item_count === 0 || snapshot.restoring) {
      const missing = missingItems(snapshot, cart);
      if (missing.length) {
        // Mark before sending: an interrupted or partial response must retry
        // only the missing quantities, never the whole original cart again.
        persist(CART, { ...snapshot, restoring: true });
        await request(config.cartUrl + '/add.js', { method: 'POST',
          headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ items: missing }) });
        const restored = await request(config.cartUrl + '.js', { cache: 'no-store' });
        if (missingItems(snapshot, restored).length) throw new Error('cart_restore_incomplete');
      }
    }
    // A surviving cart is untouched. Failed restores retain the backup.
    if (stillCurrent() && read(CART)?.token === snapshot.token) remove(CART);
  }

  let flight = null;
  function resume() {
    if (flight) return flight;
    // Serialize same-origin tabs so two return pages cannot both re-add a cart.
    const run = typeof navigator !== 'undefined' && navigator.locks
      ? navigator.locks.request('robopixel-pixelback-return', returnAfterPayment)
      : returnAfterPayment();
    flight = run.finally(() => { flight = null; });
    return flight;
  }
  async function returnAfterPayment() {
    const url = current();
    const home = config.rootUrl.replace(/\/$/, '') || '/';
    const shouldNavigate = (url.pathname.replace(/\/$/, '') || '/') === home || onCustomizer();
    const context = read(RETURN);
    if (!context) return;
    if (!fresh(context)) { remove(RETURN); remove(CART); return; }
    let token = context.token;
    try { token = token || localStorage.getItem(TOKEN); } catch (_) {}
    if (!token) return;
    const stillCurrent = () => {
      const pending = read(RETURN);
      return pending && pending.t === context.t && pending.token === context.token;
    };
    for (const delay of [0, 1000, 2500, 5000]) {
      if (delay) await new Promise(resolve => setTimeout(resolve, delay));
      if (!stillCurrent()) return;
      try {
        let deviceToken = null;
        try { deviceToken = localStorage.getItem('robopixel_device_token'); } catch (_) {}
        const result = await request(config.workerUrl + '/pixelback/claim', { method: 'POST', credentials: 'include',
          headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ token, deviceToken }) });
        if (!stillCurrent()) return;
        if (result.status === 'unknown' || result.status === 'mismatch') { remove(RETURN); remove(CART); return; }
        if (result.status !== 'ready') continue;
        await restoreCart(token, stillCurrent);
        if (!stillCurrent()) return;
        const target = new URL(config.customizerUrl, url.origin);
        for (const key of ['brand', 'model', 'method']) {
          const value = clean(context[key]);
          if (value && (key !== 'method' || methods.includes(value))) target.searchParams.set(key, value);
        }
        if (context.pair) target.searchParams.set('pair_second', '1');
        // Carry the existing preview selection when testing an unpublished theme.
        const preview = url.searchParams.get('preview_theme_id');
        if (preview && /^\d+$/.test(preview)) target.searchParams.set('preview_theme_id', preview);
        remove(RETURN);
        const same = onCustomizer() && ['brand', 'model', 'method', 'pair_second'].every(key =>
          (url.searchParams.get(key) || '') === (target.searchParams.get(key) || ''));
        document.dispatchEvent(new CustomEvent('robopixel:pixelback-returned'));
        if (shouldNavigate && !same) window.location.replace(target.pathname + target.search);
        return;
      } catch (_) { /* Bounded retries cover webhook lag and transient failures. */ }
    }
    // Retain context for pageshow/focus/next navigation; never infer a purchase.
  }
  window.RoboPixelPixelbackReturn = { remember, prepare, resume };
  remember();
  window.addEventListener('pagehide', () => remember());
  window.addEventListener('pageshow', resume);
  window.addEventListener('focus', resume);
  resume();
})();
